import React, { FunctionComponent, ReactElement } from "react";

// Styles : styles/molecules/MenuNav
import Nav from "components/atoms/Nav";
import { handleApolloError } from "hooks/handleApolloError";
import { useQuery } from "@apollo/client";
import { HEADER_QUERY } from "constants/queries/HeaderQueries";
import {
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import {
  HeaderChild,
  HeaderQueryData,
  MenuInterface,
  SubHeaderChild,
} from "interfaces/header/HeaderInterfaces";
import { NavLink } from "react-router-dom";
import { useBreakpoints } from "utils/breakpoints";
import NavLinkLayout from "./NavLinkLayout";
import { JOIN_US_PATH, NEWS_PATH, PROPERTIES_PATH } from "constants/routes/RoutePaths";
import SubMenuLeafItem from "./SubMenuLeafItem";
import SubMenuGroupItem from "./SubMenuGroupItem";

const MenuNav: FunctionComponent = (): ReactElement => {
  const { data: headerData, error: headerQueryError } =
    useQuery<HeaderQueryData>(HEADER_QUERY);
  handleApolloError("header_query", headerQueryError);

  const breakpoints = useBreakpoints();
  const isMidOrLess = breakpoints.isMdOrLess;

  const menus = headerData?.header?.menus ?? [];

  if (menus.length === 0) {
    return (
      <Nav className="MenuNav">
        <NavLinkLayout path={PROPERTIES_PATH} message="property_page" />
        <NavLinkLayout path={NEWS_PATH} message="articles_page" />
        <NavLinkLayout path={JOIN_US_PATH} message="join_us_page" />
      </Nav>
    );
  }

  return (
    <Nav className="MenuNav" navbar={isMidOrLess}>
      {menus.map((menu: MenuInterface, headerKey: number) => {
        const hasChildren = (menu.children ?? []).length > 0;

        if (!hasChildren) {
          return (
            <div key={headerKey} className="nav-item font-weight-bold">
              {menu.link ? (
                <NavLink to={menu.link} className="nav-link">
                  {menu.label}
                </NavLink>
              ) : (
                <span className="nav-link disabled" aria-disabled="true">
                  {menu.label}
                </span>
              )}
            </div>
          );
        }

        return (
        <UncontrolledDropdown
          key={headerKey}
          className="font-weight-bold"
          inNavbar
        >
          <DropdownToggle nav caret={hasChildren} className="px-5 menu-caret">
            {menu.label}
          </DropdownToggle>

          <DropdownMenu className={`m-0${isMidOrLess ? " border-0" : ""}`}>
            {(menu.children ?? []).map(
              (child: HeaderChild, childKey: number) => {
              const subItems: SubHeaderChild[] = child.children ?? [];
              const hasSub = subItems.length > 0;

              if (!hasSub) {
                return (
                  <SubMenuLeafItem
                    key={`${headerKey}-${childKey}`}
                    child={child}
                  />
                );
              }

              return (
                <SubMenuGroupItem
                  key={`${headerKey}-${childKey}`}
                  child={child}
                  subItems={subItems}
                  dropEnd={!isMidOrLess}
                />
              );
            }
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
        );
      })}
    </Nav>
  );
};

export default MenuNav;
